// One task in full: every job, every attempt at it, and why an attempt
// ended. This is the drill-down you open when a task is failing and the
// list only tells you that it is.
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminGetTask, type AdminTaskView } from "../../api/client";
import { attemptMetaRows } from "../../lib/attemptMeta";
import { formatMinor, formatWhen } from "../../lib/format";

const REFRESH_MS = 5000;
const TERMINAL = new Set(["DONE", "FAILED", "CANCELLED"]);

export default function AdminTaskDetail() {
  const { id = "" } = useParams();
  const [task, setTask] = useState<AdminTaskView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTask(await adminGetTask(id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load task");
    }
  }, [id]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (error && !task) return <p className="a-error">{error}</p>;
  if (!task) return <p className="a-loading">Loading…</p>;

  const totalAttempts = task.jobs.reduce((n, j) => n + j.attempts.length, 0);

  return (
    <>
      <div className="a-head">
        <div>
          <h1 className="a-h1">Task {task.task_id.slice(0, 8)}</h1>
          <p className="a-lede">
            {task.alias} · {task.preset} · <Link to="/admin/tasks">all tasks</Link>
          </p>
        </div>
        <span className="a-panel__meta">{TERMINAL.has(task.state) ? "terminal" : "live"}</span>
      </div>

      {error && <p className="a-error">{error}</p>}

      <div className="a-tiles">
        <div className="a-tile">
          <span className="a-tile__label">State</span>
          <span className={`a-tile__value a-tile__value--${task.state === "DONE" ? "green" : task.state === "FAILED" ? "red" : "amber"}`}>
            {task.state.toLowerCase()}
          </span>
          <span className="a-tile__sub">{formatWhen(task.created_at)}</span>
        </div>
        <div className="a-tile">
          <span className="a-tile__label">Results</span>
          <span className="a-tile__value">
            {task.done}/{task.requested}
          </span>
          <span className="a-tile__sub">
            {task.failed} failed · {task.cancelled} cancelled
          </span>
        </div>
        <div className="a-tile">
          <span className="a-tile__label">Quoted</span>
          <span className="a-tile__value">{formatMinor(task.quoted_total, task.currency)}</span>
          <span className="a-tile__sub">refunded {formatMinor(task.refunded, task.currency)}</span>
        </div>
        <div className="a-tile">
          <span className="a-tile__label">Jobs / attempts</span>
          <span className="a-tile__value">
            {task.jobs.length} / {totalAttempts}
          </span>
          <span className="a-tile__sub">{task.scheduling_class}</span>
        </div>
        <div className="a-tile">
          <span className="a-tile__label">User</span>
          <span className="a-tile__value a-tile__value--sm">{task.user_id.slice(0, 8)}</span>
          <span className="a-tile__sub">owner</span>
        </div>
      </div>

      {task.jobs.map((job) => (
        <div className="a-panel" key={job.job_id}>
          <div className="a-panel__head">
            <span className="a-panel__title">
              {job.step} · {job.job_definition}
            </span>
            <span className="a-panel__meta">
              <span className={`a-state a-state--${job.state.toLowerCase()}`}>{job.state.toLowerCase()}</span>
              {" · "}
              free {job.free_attempts} · paid {job.paid_attempts}
              {job.excluded_runtimes.length > 0 && ` · excluded ${job.excluded_runtimes.join(", ")}`}
            </span>
          </div>
          {job.attempts.length === 0 ? (
            <p className="a-empty">No attempt yet.</p>
          ) : (
            <div className="a-scroll">
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Attempt</th>
                    <th>Runtime</th>
                    <th>Deployment</th>
                    <th>State</th>
                    <th>Accepted</th>
                    <th>Finished</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {job.attempts.map((a) => (
                    <tr key={a.attempt_id}>
                      <td>{a.attempt_id.slice(0, 8)}</td>
                      <td>{a.runtime}</td>
                      <td className="a-table__dim">{a.deployment}</td>
                      <td>
                        <span className={`a-state a-state--${a.state.toLowerCase()}`}>{a.state.toLowerCase()}</span>
                      </td>
                      <td className="a-table__dim">{formatWhen(a.accepted_at)}</td>
                      <td className="a-table__dim">{a.finished_at ? formatWhen(a.finished_at) : "—"}</td>
                      <td className="a-table__hi">
                        {a.error ? `${a.error.code}: ${a.error.message}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {job.attempts
                .filter((a) => attemptMetaRows(a.outputs).length > 0)
                .map((a) => {
                  const rows = attemptMetaRows(a.outputs);
                  return (
                    <details key={`meta-${a.attempt_id}`} className="a-meta">
                      <summary>
                        Run meta — attempt {a.attempt_id.slice(0, 8)} ({rows.length} fields)
                      </summary>
                      <table className="a-table a-table--kv">
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.key}>
                              <th scope="row">{r.key}</th>
                              <td>{r.nested ? <pre className="a-meta__json">{r.value}</pre> : r.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  );
                })}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
